import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@atoms/Button';
import { Input } from '@atoms/Input';
import { Card } from '@atoms/Card';
import { Badge } from '@atoms/Badge';
import { Spinner } from '@atoms/Spinner';
import { Modal } from '@atoms/Modal';
import { adminService } from '@services/adminService';
import { storageService } from '@services/storageService';
import { AD_STATUS_LABELS, formatShortDate } from '@utils/formatters';

type TabId = 'pending' | 'all' | 'create';

export function AdminAdsPage() {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<TabId>('pending');
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [adRate, setAdRate] = useState('');
  const [createForm, setCreateForm] = useState({
    title: '',
    subtitle: '',
    image: '',
    buttonText: 'Detay',
    buttonLink: '/',
    durationHours: '24',
    sortOrder: '0',
  });

  const { data: pendingAds, isLoading: pendingLoading } = useQuery({
    queryKey: ['admin', 'pending-ads'],
    queryFn: () => adminService.getPendingAds(),
    enabled: tab === 'pending',
  });

  const { data: allAds, isLoading: allLoading } = useQuery({
    queryKey: ['admin', 'all-ads'],
    queryFn: () => adminService.getAllAds(),
    enabled: tab === 'all',
  });

  const { data: rateData } = useQuery({
    queryKey: ['admin', 'ad-rate'],
    queryFn: () => adminService.getAdRate(),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => adminService.approveAd(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'pending-ads'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'all-ads'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      adminService.rejectAd(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'pending-ads'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'all-ads'] });
      setRejectId(null);
      setRejectReason('');
    },
  });

  const createMutation = useMutation({
    mutationFn: () =>
      adminService.createAd({
        title: createForm.title,
        subtitle: createForm.subtitle || undefined,
        image: createForm.image,
        buttonText: createForm.buttonText,
        buttonLink: createForm.buttonLink,
        durationHours: Number(createForm.durationHours),
        sortOrder: Number(createForm.sortOrder),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'all-ads'] });
      setCreateForm({
        title: '',
        subtitle: '',
        image: '',
        buttonText: 'Detay',
        buttonLink: '/',
        durationHours: '24',
        sortOrder: '0',
      });
      setTab('all');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => adminService.toggleAd(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'all-ads'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminService.deleteAd(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'all-ads'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'pending-ads'] });
    },
  });

  const rateMutation = useMutation({
    mutationFn: () => adminService.setAdRate(Number(adRate)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'ad-rate'] }),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => storageService.uploadImage(file),
    onSuccess: (url) => setCreateForm((prev) => ({ ...prev, image: url })),
  });

  const loading = tab === 'pending' ? pendingLoading : tab === 'all' ? allLoading : false;
  const ads = tab === 'pending' ? pendingAds : tab === 'all' ? allAds : [];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Reklam Yönetimi</h1>
        <p className="mt-1 text-sm text-slate-500">Onay kuyruğu, aktif reklamlar ve yeni banner oluşturma</p>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {([
          ['pending', 'Onay Kuyruğu'],
          ['all', 'Tüm Reklamlar'],
          ['create', 'Yeni Reklam'],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`rounded-full px-4 py-1.5 text-sm ${
              tab === id ? 'bg-brand-500 text-white' : 'bg-gray-100'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <Card className="mb-6 flex flex-wrap items-end gap-3">
        <p className="text-sm text-gray-600">
          Günlük kredi oranı: <strong>{rateData?.creditsPerDay ?? '—'}</strong>
        </p>
        <Input
          label="Yeni oran"
          type="number"
          min={1}
          value={adRate}
          onChange={(e) => setAdRate(e.target.value)}
          className="w-32"
        />
        <Button
          size="sm"
          loading={rateMutation.isPending}
          disabled={!adRate}
          onClick={() => rateMutation.mutate()}
        >
          Kaydet
        </Button>
      </Card>

      {tab === 'create' && (
        <Card className="card-elevated mb-6 grid gap-3 md:grid-cols-2">
          <Input label="Başlık" value={createForm.title} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} />
          <Input label="Alt Başlık" value={createForm.subtitle} onChange={(e) => setCreateForm({ ...createForm, subtitle: e.target.value })} />
          <Input label="Görsel URL" value={createForm.image} onChange={(e) => setCreateForm({ ...createForm, image: e.target.value })} className="md:col-span-2" />
          <div className="md:col-span-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadMutation.mutate(file);
              }}
            />
            <Button type="button" variant="outline" loading={uploadMutation.isPending} onClick={() => fileRef.current?.click()}>
              Görsel Yükle
            </Button>
            {createForm.image && (
              <img src={createForm.image} alt="" className="mt-2 h-24 w-40 rounded-lg object-cover" />
            )}
          </div>
          <Input label="Buton Metni" value={createForm.buttonText} onChange={(e) => setCreateForm({ ...createForm, buttonText: e.target.value })} />
          <Input label="Buton Link" value={createForm.buttonLink} onChange={(e) => setCreateForm({ ...createForm, buttonLink: e.target.value })} />
          <Input label="Süre (saat)" type="number" value={createForm.durationHours} onChange={(e) => setCreateForm({ ...createForm, durationHours: e.target.value })} />
          <Input label="Sıra" type="number" value={createForm.sortOrder} onChange={(e) => setCreateForm({ ...createForm, sortOrder: e.target.value })} />
          <Button
            loading={createMutation.isPending}
            disabled={!createForm.title || !createForm.image}
            onClick={() => createMutation.mutate()}
          >
            Oluştur
          </Button>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Spinner size="lg" /></div>
      ) : tab !== 'create' && !ads?.length ? (
        <Card className="text-center text-gray-500">Reklam bulunamadı</Card>
      ) : tab !== 'create' ? (
        <div className="space-y-4">
          {ads!.map((ad) => (
            <Card key={ad.id} className="flex flex-col gap-4 md:flex-row">
              <img src={ad.image} alt={ad.title} className="h-32 w-full rounded-lg object-cover md:w-48" />
              <div className="flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-semibold">{ad.title}</h3>
                    {ad.subtitle && <p className="text-sm text-gray-600">{ad.subtitle}</p>}
                    <p className="mt-1 text-xs text-gray-500">
                      {ad.wholesaler?.name ?? 'Admin'} · {formatShortDate(ad.createdAt!)}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge variant={ad.isActive ? 'success' : 'default'}>
                      {ad.isActive ? 'Aktif' : 'Pasif'}
                    </Badge>
                    <Badge variant="warning">{AD_STATUS_LABELS[ad.status]}</Badge>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {tab === 'pending' && (
                    <>
                      <Button size="sm" loading={approveMutation.isPending} onClick={() => approveMutation.mutate(ad.id)}>
                        Onayla
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => setRejectId(ad.id)}>Reddet</Button>
                    </>
                  )}
                  {tab === 'all' && (
                    <>
                      <Button size="sm" variant="outline" loading={toggleMutation.isPending} onClick={() => toggleMutation.mutate(ad.id)}>
                        {ad.isActive ? 'Pasifleştir' : 'Aktifleştir'}
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => deleteMutation.mutate(ad.id)}>Sil</Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : null}

      <Modal open={Boolean(rejectId)} onClose={() => setRejectId(null)} title="Reddetme Nedeni">
        <div className="space-y-3">
          <Input label="Neden" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
          <Button
            variant="danger"
            loading={rejectMutation.isPending}
            disabled={!rejectReason.trim()}
            onClick={() => rejectId && rejectMutation.mutate({ id: rejectId, reason: rejectReason })}
          >
            Reddet
          </Button>
        </div>
      </Modal>
    </div>
  );
}
